import React, { useCallback } from 'react';

import { ErrorMessage } from '@eika/attention-message';
import { Button, SmallButton } from '@eika/button';
import { ErrorCard } from '@eika/card';
import { useViewType, ViewType } from '@eika/hooks';
import { Loader } from '@eika/loader';

import { useBankCountries, useCountryDetails } from '../../../../fetchHooks/useCountryDetails';
import { useValidateBank, useValidateIban } from '../../../../fetchHooks/useCrossBorderBankValidation';
import type { BankValidation, CountryDetails } from '../../../../generated-types';
import useTranslation from '../../../../hooks/useTranslation';
import { NavigationButtons } from '../../../shared/NavigationButtons';
import { CrossBorderPaymentActionType } from '../form/actions';
import useFormContext from '../form/useFormContext';
import { receiverError, validateUpdateReceiver } from '../form/validate';

interface Props {
    setValidationErrors: (errors: receiverError) => void;
    setRequiredCreditorDetails: (value: boolean) => void;
}

const CrossBorderReceiverBankDetails: React.FC<Props> = ({ setValidationErrors, setRequiredCreditorDetails }) => {
    const translated = useTranslation();
    const viewType = useViewType();
    const { data: bankCountryList, isLoading: loadingBankCountries } = useBankCountries();
    const { state, handleChange } = useFormContext();
    const { error: bankDetailsError, refetch: refetchBankDetails } = useCountryDetails(
        state.receiver?.bankCountryCode,
        useCallback(
            (data: CountryDetails) => {
                handleChange({
                    type: CrossBorderPaymentActionType.SET_RECEIVER_COUNTRY_INFO,
                    payload: data,
                });
                setRequiredCreditorDetails(data.requiresCreditorDetails);
            },
            [handleChange, setRequiredCreditorDetails],
        ),
    );

    const onValidateBank = useCallback(
        (data: BankValidation) =>
            handleChange({
                type: CrossBorderPaymentActionType.SET_BANK_VALIDATION,
                payload: data,
            }),
        [handleChange],
    );
    const {
        data: ibanValidation,
        error: ibanValidationError,
        isLoading: loadingIbanValidation,
        refetch: retryIban,
    } = useValidateIban(
        state.receiver?.accountNumber ? { value: state.receiver.accountNumber } : undefined,
        state.receiver?.accountType === 'IBAN',
        onValidateBank,
    );
    const {
        data: bankValidation,
        error: bankValidationError,
        isLoading: loadingBankValidation,
        refetch: retryBank,
    } = useValidateBank(
        state.receiver?.bic,
        state.receiver?.nationalId,
        state.receiver?.bankCountryCode,
        state.receiver?.accountType === 'BBAN',
        onValidateBank,
    );

    const bankInformation = ibanValidation || bankValidation;

    const receiverBankCountry = bankCountryList?.find(
        (country) => country.countryCode.value === bankInformation?.bank?.countryCode.value,
    );

    const validateFields = () => {
        const validations = validateUpdateReceiver(translated, state);
        setValidationErrors(validations);
        return !(validations.receiverName || validations.creditorDetails || validations.receiverCountryCode);
    };

    if (ibanValidationError || bankValidationError) {
        return (
            <ErrorCard
                className="select-cross-border-receiver__fetch-receivers-error max-desktop-width"
                title={translated.errors.somethingWentWrong}
                description={translated.crossBorderPayment.newReceiver.errorBankValidation}
            >
                <Button size="small" onClick={() => (ibanValidationError ? retryIban() : retryBank())}>
                    {translated.tryAgain}
                </Button>
            </ErrorCard>
        );
    }

    if (loadingBankValidation || loadingIbanValidation || loadingBankCountries) {
        return <Loader className="cross-border-receiver-bank-details__loader" mini />;
    }

    const bic = bankInformation?.bank?.bic;

    return (
        <>
            <dl className="cross-border-receiver-details__description max-desktop-width">
                <dt className="cross-border-receiver-details__term">
                    {state.receiver?.accountType === 'IBAN'
                        ? translated.crossBorderPayment.newReceiver.ibanLabel
                        : translated.crossBorderPayment.newReceiver.bbanLabel}
                </dt>
                <dd className="cross-border-receiver-details__term-description">{state.receiver?.accountNumber}</dd>
                {bic?.value && (
                    <>
                        <dt className="cross-border-receiver-details__term">
                            {translated.crossBorderPayment.newReceiver.bicLabel}
                        </dt>
                        <dd className="cross-border-receiver-details__term-description">{bic?.value}</dd>
                    </>
                )}

                <dt className="cross-border-receiver-details__term">
                    {translated.crossBorderPayment.newReceiver.nationalIdLabel}
                </dt>
                <dd className="cross-border-receiver-details__term-description">
                    {state.receiver?.nationalId?.value || bankInformation?.bank?.nationalId?.value}
                </dd>
                <dt className="cross-border-receiver-details__term">
                    {translated.crossBorderPayment.newReceiver.receiverBank}
                </dt>
                <dd className="cross-border-receiver-details__bank-description">{bankInformation?.bank?.name}</dd>
                <dd className="cross-border-receiver-details__bank-description">{bankInformation?.bank?.address}</dd>
                <dd className="cross-border-receiver-details__bank-description">
                    {receiverBankCountry?.name.toUpperCase()}
                </dd>
            </dl>
            {!bankInformation?.valid && (
                <ErrorMessage text={translated.crossBorderPayment.newReceiver.errorValidToPayReceiver} />
            )}
            {bankDetailsError && (
                <ErrorMessage
                    text={translated.crossBorderPayment.newReceiver.bankDetailsError}
                    clickableItem={
                        <SmallButton size="small" buttonType="negative" onClick={() => refetchBankDetails()}>
                            {translated.tryAgain}
                        </SmallButton>
                    }
                />
            )}
            {(bankInformation?.valid || bankDetailsError) && (
                <NavigationButtons
                    nextUrl={viewType === ViewType.mobile ? 'belop' : 'nettbank-belop'}
                    centerOnSmallerWidth={viewType !== ViewType.mobile}
                    onClick={validateFields}
                />
            )}
        </>
    );
};

export default CrossBorderReceiverBankDetails;
